import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/practiceQuizzes.css';
import { useCurrentUser } from '../contexts/UserContext';
import { getStudentEnrollments } from '../services/enrollmentService';
import { listCourses } from '../services/courseService';
import { getModulesForCourse } from '../services/moduleService';
import { getQuestions, getTopicStats, getSessions, getSessionReplay } from '../services/questionService';

const STAFF_ROLES = new Set(['teacher', 'mentor-lead', 'sales', 'admin']);

const LENGTH_OPTIONS = [
  { id: 'short', label: 'Short', questions: 12 },
  { id: 'standard', label: 'Medium', questions: 24 },
  { id: 'extended', label: 'Long', questions: 36 },
];

const SAT_MODULES = [
  { section: 'Reading & Writing', module: 1, questions: 27, minutes: 32 },
  { section: 'Reading & Writing', module: 2, questions: 27, minutes: 32 },
  { section: 'Math', module: 1, questions: 22, minutes: 35 },
  { section: 'Math', module: 2, questions: 22, minutes: 35 },
];

function timeAgo(date) {
  if (!date) return '—';
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

const shuffleArray = (input) => {
  const array = [...input];
  for (let index = array.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [array[index], array[swapIndex]] = [array[swapIndex], array[index]];
  }
  return array;
};

function SatPracticeCard({ course }) {
  const navigate = useNavigate();
  return (
    <section className="practice-panel sat-practice-card">
      <p className="eyebrow">SAT Exam</p>
      <div className="sat-sections">
        {[
          { label: 'Reading & Writing', questions: 27, minutes: 32 },
          { label: 'Math', questions: 22, minutes: 35 },
        ].map((s) => (
          <div key={s.label} className="sat-section-row">
            <span className="sat-section-name">{s.label}</span>
            <div className="sat-module-flow">
              <span className="sat-mod">M1</span>
              <span className="sat-arrow">→</span>
              <span className="sat-mod sat-mod--adaptive">M2</span>
            </div>
            <span className="sat-section-meta">{s.questions}Q · {s.minutes}min</span>
          </div>
        ))}
      </div>
      <div className="practice-footer-row">
        <span className="muted-text sat-footer-note">Stage-adaptive · 98 questions · ~2h 14min</span>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/practice/sat-session', {
            state: { courseId: course.id, courseLabel: course.label },
          })}
        >
          Start Exam
        </button>
      </div>
    </section>
  );
}

export default function PracticePage() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isStaff = STAFF_ROLES.has(currentUser?.role);
  const studentId = currentUser?.role === 'student' ? currentUser?.id || currentUser?._id : null;
  const userId = currentUser?.id || currentUser?._id || null;

  const [courseOptions, setCourseOptions] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [courseModules, setCourseModules] = useState([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [lengthId, setLengthId] = useState('standard');
  const [statusMessage, setStatusMessage] = useState('');
  const [isModuleMenuOpen, setIsModuleMenuOpen] = useState(false);
  const moduleMenuRef = useRef(null);
  const [topicStats, setTopicStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [replayError, setReplayError] = useState('');

  // Fetch course list — all courses for staff, enrolled courses for students
  useEffect(() => {
    let cancelled = false;

    if (isStaff) {
      listCourses()
        .then((data) => {
          if (cancelled) return;
          const options = (data?.courses || []).map((c) => ({
            id: String(c._id),
            label: c.title || c.subject || 'Course',
            examType: c.examType || null,
          }));
          setCourseOptions(options);
        })
        .catch(() => { if (!cancelled) setCourseOptions([]); });
    } else if (studentId) {
      getStudentEnrollments(studentId)
        .then((data) => {
          if (cancelled) return;
          const options = (data?.enrollments || [])
            .map((e) => {
              const course = e.course || {};
              const id = String(course.id || course._id || '');
              if (!id) return null;
              return {
                id,
                label: course.title || course.subject || 'Course',
                examType: course.examType || null,
              };
            })
            .filter(Boolean);
          setCourseOptions(options);
        })
        .catch(() => { if (!cancelled) setCourseOptions([]); });
    } else {
      setCourseOptions([]);
    }

    return () => { cancelled = true; };
  }, [isStaff, studentId]);

  const satCourses = useMemo(
    () => courseOptions.filter((c) => c.examType === 'sat'),
    [courseOptions],
  );

  const generalCourses = useMemo(
    () => courseOptions.filter((c) => c.examType !== 'sat'),
    [courseOptions],
  );

  // Set default courseId from general courses only
  useEffect(() => {
    if (generalCourses.length && !courseId) {
      setCourseId(generalCourses[0].id);
    }
  }, [generalCourses, courseId]);

  // Fetch modules for selected general course
  useEffect(() => {
    if (!courseId) {
      setCourseModules([]);
      setSelectedModuleIds([]);
      return;
    }
    getModulesForCourse(courseId)
      .then((data) => {
        const mods = data?.modules || [];
        setCourseModules(mods);
        setSelectedModuleIds(mods.map((m) => String(m._id || m.id)));
      })
      .catch(() => {
        setCourseModules([]);
        setSelectedModuleIds([]);
      });
  }, [courseId]);

  // Fetch questions for the selected general course
  useEffect(() => {
    if (!courseId) {
      setQuestions([]);
      return;
    }
    getQuestions({ courseId, userId: studentId || undefined })
      .then((data) => setQuestions(data?.questions || []))
      .catch(() => setQuestions([]));
  }, [courseId]);

  useEffect(() => {
    setStatusMessage('');
  }, [courseId, selectedModuleIds, lengthId]);

  // Fetch topic stats for the analytics panel — independent of builder filters
  useEffect(() => {
    if (!userId) {
      setTopicStats([]);
      return;
    }
    let cancelled = false;
    setStatsLoading(true);
    getTopicStats({ userId: String(userId) })
      .then((data) => { if (!cancelled) setTopicStats(data?.stats || []); })
      .catch(() => { if (!cancelled) setTopicStats([]); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    if (!userId) { setSessions([]); return; }
    let cancelled = false;
    setSessionsLoading(true);
    getSessions({ userId: String(userId) })
      .then((data) => { if (!cancelled) setSessions(data?.sessions || []); })
      .catch(() => { if (!cancelled) setSessions([]); })
      .finally(() => { if (!cancelled) setSessionsLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const handleReplay = useCallback(async (sessionId) => {
    setReplayError('');
    try {
      const data = await getSessionReplay(sessionId, String(userId));
      const questions = data.attempts.map((a) => a.questionId).filter((q) => q?._id);
      const replayAnswers = Object.fromEntries(
        data.attempts
          .filter((a) => a.selectedAnswer && a.questionId?._id)
          .map((a) => [String(a.questionId._id), a.selectedAnswer]),
      );
      navigate('/practice/session', {
        state: {
          quiz: {
            questions,
            courseLabel: data.courseLabel,
            moduleLabels: data.moduleLabels,
            generatedAt: sessionId,
          },
          replay: true,
          replayAnswers,
        },
      });
    } catch (err) {
      setReplayError('Could not load that session. Try again.');
    }
  }, [userId, navigate]);

  // Close module menu on outside click / Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moduleMenuRef.current && !moduleMenuRef.current.contains(e.target)) {
        setIsModuleMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsModuleMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const moduleQuestionCounts = useMemo(() => {
    const counts = {};
    questions.forEach((q) => {
      const mid = String(q.moduleId);
      counts[mid] = (counts[mid] || 0) + 1;
    });
    return counts;
  }, [questions]);

  const questionPool = useMemo(() => {
    if (!selectedModuleIds.length) return questions;
    const moduleSet = new Set(selectedModuleIds);
    return questions.filter((q) => moduleSet.has(String(q.moduleId)));
  }, [questions, selectedModuleIds]);

  const moduleTriggerLabel = useMemo(() => {
    if (!selectedModuleIds.length) return 'Select modules';
    if (selectedModuleIds.length === courseModules.length) return 'All modules';
    if (selectedModuleIds.length === 1) {
      const mod = courseModules.find((m) => String(m._id || m.id) === selectedModuleIds[0]);
      return mod?.title || '1 module';
    }
    return `${selectedModuleIds.length} modules selected`;
  }, [selectedModuleIds, courseModules]);

  const handleGenerateQuiz = () => {
    if (!questionPool.length) {
      setStatusMessage('No questions available for the selected modules. Check back soon!');
      return;
    }
    const desired = LENGTH_OPTIONS.find((o) => o.id === lengthId)?.questions || 24;
    const selection = shuffleArray(questionPool).slice(0, Math.min(desired, questionPool.length));
    const courseLabel = generalCourses.find((o) => o.id === courseId)?.label || '';
    const moduleLabels = courseModules
      .filter((m) => selectedModuleIds.includes(String(m._id || m.id)))
      .map((m) => ({ id: String(m._id || m.id), title: m.title }));
    navigate('/practice/session', {
      state: {
        quiz: {
          questions: selection,
          generatedAt: new Date().toISOString(),
          courseId,
          courseLabel,
          moduleIds: selectedModuleIds,
          moduleLabels,
          lengthId,
          requestedCount: desired,
          availableCount: questionPool.length,
        },
      },
    });
  };

  return (
    <div className="practice-page practice-builder-only">
      <section className="practice-panel practice-panel--outer full-width">
        <p className="eyebrow">Quiz Builder</p>
        <div className="practice-cards-row">

          {satCourses.map((course) => (
            <SatPracticeCard key={course.id} course={course} />
          ))}

          {generalCourses.length > 0 && (
            <div className="practice-panel practice-panel--builder">
          <p className="eyebrow">Practice</p>

          <div className="practice-control-row">
            <div className="practice-field compact-field">
              <label className="eyebrow" htmlFor="practice-course">Course</label>
              <select
                id="practice-course"
                className="course-filter-select"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                disabled={!generalCourses.length}
              >
                {!generalCourses.length && <option value="">No courses enrolled</option>}
                {generalCourses.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="practice-field modules-field" ref={moduleMenuRef}>
              <label className="eyebrow">Modules</label>
              <div className={`custom-select ${isModuleMenuOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className="select-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={isModuleMenuOpen}
                  onClick={() => setIsModuleMenuOpen((prev) => !prev)}
                >
                  <span>{moduleTriggerLabel}</span>
                  <span className="select-caret" aria-hidden="true">▾</span>
                </button>
                {isModuleMenuOpen && (
                  <ul className="select-menu" role="listbox" aria-multiselectable="true">
                    {courseModules.length === 0 && (
                      <li>
                        <span className="select-option" style={{ opacity: 0.5, cursor: 'default' }}>
                          No modules available
                        </span>
                      </li>
                    )}
                    {courseModules.map((module) => {
                      const mid = String(module._id || module.id);
                      const isSelected = selectedModuleIds.includes(mid);
                      const count = moduleQuestionCounts[mid] || 0;
                      return (
                        <li key={mid}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`select-option selectable ${isSelected ? 'selected' : ''}`}
                            onClick={() =>
                              setSelectedModuleIds((prev) =>
                                isSelected ? prev.filter((id) => id !== mid) : [...prev, mid],
                              )
                            }
                          >
                            <span className="check-indicator" aria-hidden="true">
                              {isSelected ? '✔' : ''}
                            </span>
                            <span>{module.title}</span>
                            <span className="option-meta" aria-hidden="true">{count} Qs</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="practice-field length-field">
              <label className="eyebrow">Test length</label>
              <div className="combo-pill">
                {LENGTH_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`combo-pill-option ${lengthId === option.id ? 'active' : ''}`}
                    onClick={() => setLengthId(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="practice-footer-row">
            {statusMessage && <p className="practice-banner">{statusMessage}</p>}
            <button type="button" className="btn btn-primary" onClick={handleGenerateQuiz}>
              Generate
            </button>
          </div>
        </div>
          )}
        </div>
      </section>

      {userId && (
        <section className="practice-panel practice-panel--outer practice-panel--analytics full-width">
          <p className="eyebrow">Analytics</p>

          <div className="analytics-panels">

            {/* Left — Module Mastery */}
            <div className="analytics-panel">
              <p className="analytics-panel-heading eyebrow">Module Mastery</p>
              {statsLoading && <p className="analytics-empty">Loading…</p>}
              {!statsLoading && topicStats.length === 0 && (
                <p className="analytics-empty">No data yet. Take a quiz to see your module stats.</p>
              )}
              {!statsLoading && topicStats.length > 0 && (
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th className="col-topic">Module</th>
                        <th className="col-num">Quizzes</th>
                        <th className="col-num">Questions</th>
                        <th className="col-num">Correct</th>
                        <th className="col-num">Wrong</th>
                        <th className="col-accuracy">Accuracy</th>
                        <th className="col-recency">Last Practiced</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topicStats.map((s) => {
                        const moduleTitle = s.moduleId?.title || 'Unknown module';
                        const wrong = s.totalAttempts - s.correctCount;
                        const accuracy = s.totalAttempts > 0
                          ? Math.round((s.correctCount / s.totalAttempts) * 100)
                          : 0;
                        const accuracyClass = accuracy >= 80 ? 'acc-high' : accuracy >= 50 ? 'acc-mid' : 'acc-low';
                        return (
                          <tr key={String(s._id)}>
                            <td className="col-topic">{moduleTitle}</td>
                            <td className="col-num">{s.sessionCount ?? 0}</td>
                            <td className="col-num">{s.totalAttempts}</td>
                            <td className="col-num correct-val">{s.correctCount}</td>
                            <td className="col-num wrong-val">{wrong}</td>
                            <td className="col-accuracy">
                              <span className={`acc-pill ${accuracyClass}`}>{accuracy}%</span>
                            </td>
                            <td className="col-recency">{timeAgo(s.lastAttemptAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Right — Session Summary */}
            <div className="analytics-panel analytics-panel--sessions">
              <p className="analytics-panel-heading eyebrow">Session Summary</p>
              {sessionsLoading && <p className="analytics-empty">Loading…</p>}
              {!sessionsLoading && sessions.length === 0 && (
                <p className="analytics-empty">No sessions yet.</p>
              )}
              {replayError && <p className="analytics-empty analytics-error">{replayError}</p>}
              {!sessionsLoading && sessions.length > 0 && (
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th className="col-topic">Date</th>
                        <th className="col-topic">Course</th>
                        <th className="col-num">Score</th>
                        <th className="col-accuracy">Accuracy</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((s) => {
                        const accuracy = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
                        const accuracyClass = accuracy >= 80 ? 'acc-high' : accuracy >= 50 ? 'acc-mid' : 'acc-low';
                        return (
                          <tr key={s.sessionId}>
                            <td className="col-recency">{timeAgo(s.answeredAt)}</td>
                            <td className="col-topic">{s.courseLabel}</td>
                            <td className="col-num">{s.correct}/{s.total}</td>
                            <td className="col-accuracy">
                              <span className={`acc-pill ${accuracyClass}`}>{accuracy}%</span>
                            </td>
                            <td className="col-action">
                              <button className="replay-btn" onClick={() => handleReplay(s.sessionId)}>
                                Review
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </section>
      )}
    </div>
  );
}
